import { useEffect } from "react";
import type { Locale, PageKind } from "../content/types";
import { localePath } from "../i18n/routes";

type PageMetaProps = {
  title: string;
  description: string;
  locale: Locale;
  kind: PageKind;
};

const ORIGIN = "https://luxoria.axqora.com";

function setMeta(selector: string, attrs: Record<string, string>) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }
  Object.entries(attrs).forEach(([key, value]) => element?.setAttribute(key, value));
}

function setLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
  let element = document.head.querySelector<HTMLLinkElement>(selector);
  if (!element) {
    element = document.createElement("link");
    element.rel = rel;
    if (hreflang) element.hreflang = hreflang;
    document.head.appendChild(element);
  }
  element.href = href;
}

export function PageMeta({ title, description, locale, kind }: PageMetaProps) {
  useEffect(() => {
    const enPath = localePath("en", kind);
    const zhPath = localePath("zh", kind);
    const canonical = `${ORIGIN}${localePath(locale, kind)}`;

    document.documentElement.lang = locale === "zh" ? "zh-CN" : "en";
    document.title = title;
    setMeta('meta[name="description"]', { name: "description", content: description });
    setMeta('meta[property="og:title"]', { property: "og:title", content: title });
    setMeta('meta[property="og:description"]', { property: "og:description", content: description });
    setMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    setMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    setMeta('meta[property="og:image"]', { property: "og:image", content: `${ORIGIN}/products/luxoria/og-image.png` });
    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    setLink("canonical", canonical);
    setLink("alternate", `${ORIGIN}${enPath}`, "en");
    setLink("alternate", `${ORIGIN}${zhPath}`, "zh-CN");
    setLink("alternate", `${ORIGIN}${enPath}`, "x-default");
  }, [description, kind, locale, title]);

  return null;
}
